import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Avatar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

/**
 * RoundWinnerModal - Shows when a team wins a round/hand (but not the match)
 * Replaces the toast notification to give the user time to see the result
 * and control when to proceed to the next game.
 */
const RoundWinnerModal = ({ open, winnerTeamId, gameName, nextGameName, players, teamColors, onContinue }) => {
    const { t } = useTranslation();

    // Determine winning players based on team ID
    // Team 1: Players 0 and 2
    // Team 2: Players 1 and 3
    const winningPlayers = winnerTeamId === 1
        ? [players?.[0], players?.[2]]
        : [players?.[1], players?.[3]];

    // Get the team color (as CSS string like '#667eea')
    const winnerColorHex = winnerTeamId === 1 
        ? (typeof teamColors?.team1 === 'string' ? teamColors.team1 : '#667eea')
        : (typeof teamColors?.team2 === 'string' ? teamColors.team2 : '#f5576c');

    // Check if avatar is an emoji (string) or an image URL
    const isEmojiAvatar = (avatar) => {
        return typeof avatar === 'string' && !avatar.startsWith('http') && !avatar.startsWith('/');
    };

    return (
        <Dialog
            open={open}
            PaperProps={{
                sx: {
                    background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '20px',
                    border: `3px solid ${winnerColorHex}`,
                    boxShadow: `0 0 30px ${winnerColorHex}40, 0 10px 40px rgba(0,0,0,0.5)`,
                    maxWidth: '420px',
                    width: '90%',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Decorative top bar with team color */}
            <Box sx={{
                height: '6px',
                background: `linear-gradient(90deg, ${winnerColorHex} 0%, ${winnerColorHex}80 50%, ${winnerColorHex} 100%)`,
            }} />

            <DialogContent sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 2,
                padding: '28px 24px 20px'
            }}>
                {/* Trophy icon with glow */}
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${winnerColorHex}30 0%, transparent 70%)`,
                        animation: 'pulse 2s infinite'
                    }} />
                    <EmojiEventsIcon 
                        sx={{ 
                            fontSize: 64, 
                            color: '#FFD700',
                            filter: 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.6))',
                            zIndex: 1
                        }} 
                    />
                </Box>

                {/* Title */}
                <Typography 
                    variant="h5" 
                    sx={{ 
                        color: '#ffffff', 
                        fontWeight: 'bold', 
                        textShadow: `0 0 20px ${winnerColorHex}`,
                        fontSize: { xs: '1.4rem', sm: '1.6rem' },
                        letterSpacing: '0.5px'
                    }}
                >
                    {t('round.winner_title', { teamId: winnerTeamId, gameName: gameName || 'Ronda' })}
                </Typography>

                {/* Winning players avatars */}
                <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                    {winningPlayers.map((player, index) => (
                        player && (
                            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {isEmojiAvatar(player?.avatar) ? (
                                    <Box
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            borderRadius: '50%',
                                            border: `3px solid ${winnerColorHex}`,
                                            boxShadow: `0 0 20px ${winnerColorHex}60`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '2rem',
                                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                            backdropFilter: 'blur(4px)'
                                        }}
                                    >
                                        {player?.avatar}
                                    </Box>
                                ) : (
                                    <Avatar
                                        src={player?.avatar}
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            border: `3px solid ${winnerColorHex}`,
                                            boxShadow: `0 0 20px ${winnerColorHex}60`
                                        }}
                                    />
                                )}
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'rgba(255, 255, 255, 0.95)', 
                                        mt: 1, 
                                        fontWeight: 600,
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    {player?.name || t('setup.player_placeholder', { number: index + 1 })}
                                </Typography>
                            </Box>
                        )
                    ))}
                </Box>

                {/* Next game info */}
                {nextGameName && (
                    <Box sx={{
                        mt: 2,
                        px: 3,
                        py: 1,
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.15)'
                    }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'rgba(255, 255, 255, 0.85)',
                                fontSize: '0.95rem',
                                fontWeight: 500
                            }}
                        >
                            {t('round.next_game', { nextGameName })}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ pb: 3, pt: 0, justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={onContinue}
                    sx={{
                        background: `linear-gradient(135deg, ${winnerColorHex} 0%, ${winnerColorHex}CC 100%)`,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.05rem',
                        padding: '12px 48px',
                        borderRadius: '30px',
                        textTransform: 'none',
                        boxShadow: `0 4px 20px ${winnerColorHex}50`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${winnerColorHex}EE 0%, ${winnerColorHex} 100%)`,
                            boxShadow: `0 6px 25px ${winnerColorHex}70`,
                            transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    {t('round.continue')}
                </Button>
            </DialogActions>

            {/* CSS animation for pulse effect */}
            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 0.5; }
                    50% { transform: scale(1.2); opacity: 0.3; }
                    100% { transform: scale(1); opacity: 0.5; }
                }
            `}</style>
        </Dialog>
    );
};

export default RoundWinnerModal;
